import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: '¿Qué tipo de empresas pueden trabajar con SafeLine?',
    answer:
      'Trabajamos con empresas contratistas, subcontratistas y organizaciones que operan en sectores como energía, construcción, mantenimiento industrial, logística, montaje y servicios técnicos que requieren cumplir exigencias documentales, operativas y preventivas.',
  },
  {
    question: '¿Qué incluye el servicio de gestión documental y acreditación?',
    answer:
      'Incluye revisión, orden y gestión documental de empresa, trabajadores y vehículos, control de vigencias, apoyo en plataformas de mandantes y acompañamiento para reducir observaciones, rechazos y retrasos operativos.',
  },
  {
    question: '¿Qué incluye una matriz de riesgos?',
    answer:
      'Incluye la identificación de peligros, evaluación de riesgos, definición de controles y estructuración del documento de acuerdo con la operación real de la empresa, para que sea útil tanto en terreno como en auditoría.',
  },
  {
    question: '¿Qué incluye una matriz de cumplimiento legal?',
    answer:
      'Incluye la identificación de normativa aplicable, organización de requisitos legales, estructura de control y base documental para facilitar el seguimiento del cumplimiento legal según la actividad y operación de cada empresa.',
  },
  {
    question: '¿SafeLine puede apoyar aunque no tengamos área HSE interna?',
    answer:
      'Sí. Podemos apoyar como soporte externo en tareas documentales, matrices, auditorías o gestión preventiva, adaptándonos al nivel de estructura y necesidad de cada empresa.',
  },
  {
    question: '¿El servicio puede ser remoto o presencial?',
    answer:
      'Sí. Dependiendo del servicio y la necesidad del cliente, el apoyo puede realizarse de forma remota, presencial o mixta, permitiendo adaptarnos a requerimientos documentales y también a necesidades operativas en terreno.',
  },
  {
    question: '¿Cómo se inicia el trabajo con SafeLine?',
    answer:
      'El trabajo comienza con una revisión breve de la necesidad del cliente. A partir de eso, se define el alcance, la modalidad de apoyo y la solución más adecuada según el tipo de operación, exigencia documental o requerimiento HSE.',
  },
];

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}

function FAQItem({ question, answer, isOpen, onToggle, index }: FAQItemProps) {
  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all duration-300 ${
        isOpen ? 'border-teal-300 bg-teal-50/40' : 'border-gray-200 bg-white hover:border-teal-200'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-4">
          <span className="text-xs font-bold text-teal-600 bg-teal-100 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="text-gray-900 font-semibold text-base leading-snug">{question}</span>
        </div>
        <span
          className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${
            isOpen ? 'bg-teal-600 text-white rotate-45' : 'bg-gray-100 text-gray-500'
          }`}
        >
          <i className="ri-add-line text-lg"></i>
        </span>
      </button>

      <div
        className={`transition-all duration-300 overflow-hidden ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="px-7 pb-6 text-gray-600 text-sm leading-relaxed pl-[4.75rem]">
          {answer}
        </p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="preguntas-frecuentes" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* Left column */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Preguntas Frecuentes
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-5 leading-tight">
              Todo lo que necesitas <span className="text-teal-600">saber sobre HSE</span>
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              Resolvemos las dudas más comunes sobre nuestros servicios de consultoría HSE, gestión documental y cumplimiento normativo en Latinoamérica.
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-all whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
            >
              <i className="ri-chat-1-line"></i>
              ¿Tienes otra pregunta?
            </Link>
          </div>

          {/* Right column — accordion */}
          <div className="lg:col-span-8 flex flex-col gap-3">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                index={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() => toggle(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
